import { useEffect } from 'react';

// Keeps the POS "Scan barcode or enter SKU" field focused so a hardware scanner can fire at any
// moment. Focus is handed back whenever it drifts to a non-typing element (button, blank area,
// document body, after a dialog closes). It backs off while a Radix dialog/menu/popover is open
// or while the cashier is typing in another input, so those still work normally.
const TYPING_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

const isTypingTarget = (el) => !!el && (TYPING_TAGS.has(el.tagName) || el.isContentEditable);

const overlayOpen = () => !!document.querySelector(
  '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-radix-popper-content-wrapper], [data-scan-focus-hold]'
);

export function useScanFocusGuard(scanInputRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const refocus = () => {
      const el = scanInputRef.current;
      if (!el || !el.isConnected || el.disabled) return;
      if (document.activeElement === el) return;
      if (overlayOpen()) return;
      const active = document.activeElement;
      if (active && active !== document.body && isTypingTarget(active)) return;
      el.focus({ preventScroll: true });
    };
    const deferred = () => setTimeout(refocus, 0);
    document.addEventListener('focusout', deferred);
    document.addEventListener('pointerup', deferred);
    window.addEventListener('focus', deferred);
    const id = setInterval(refocus, 1000);
    deferred();
    return () => {
      document.removeEventListener('focusout', deferred);
      document.removeEventListener('pointerup', deferred);
      window.removeEventListener('focus', deferred);
      clearInterval(id);
    };
  }, [scanInputRef, enabled]);
}

export default useScanFocusGuard;
